import { motion } from 'framer-motion'
import ScrollReveal from '../shared/ScrollReveal'
import GradientText from '../shared/GradientText'
import { Icons } from '../shared/Icons'
import './FeatureSections.css'

export default function LandSection() {
  const features = [
    { icon: Icons.bolt, title: 'Real-time Streaming', desc: 'WebSocket-powered live data updates' },
    { icon: Icons.plug, title: 'Multi-Inverter', desc: 'Fronius, Huawei + SMA' },
    { icon: Icons.battery, title: 'Battery Control', desc: 'SOC/SOH monitoring & management' },
    { icon: Icons.chart, title: 'DC String Analysis', desc: 'MPPT tracking per string' }
  ]

  return (
    <section className="feature-section feature-section-land" id="land">
      {/* Land · Air · Space badge - positioned in upper right */}
      <motion.div
        className="section-badge section-badge-top-right"
        initial={{ opacity: 0, y: -10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <span className="badge-dot" />
        Land · Air · Space
      </motion.div>
      <div className="container">
        <div className="feature-grid feature-grid-reverse">
          <ScrollReveal animation="fadeLeft" className="feature-visual">
            <div className="feature-card-stack">
              {/* Inverter visualization */}
              <motion.div
                className="visual-card visual-card-primary"
                whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)' }}
              >
                <div className="visual-card-header">
                  <span className="visual-card-icon">
                    <Icons.cpu size={20} />
                  </span>
                  <span className="visual-card-title">Fronius Primo</span>
                  <span className="status-dot status-online" />
                </div>
                <div className="visual-metrics">
                  <div className="metric">
                    <span className="metric-value">8.2</span>
                    <span className="metric-unit">kW</span>
                    <span className="metric-label">Power Output</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">97.3</span>
                    <span className="metric-unit">%</span>
                    <span className="metric-label">Efficiency</span>
                  </div>
                </div>
                <div className="visual-chart">
                  <svg viewBox="0 0 200 60" className="mini-chart">
                    <path
                      d="M0 50 Q25 40 50 35 T100 25 T150 20 T200 15"
                      fill="none"
                      stroke="#FFD700"
                      strokeWidth="2"
                    />
                    <path
                      d="M0 50 Q25 40 50 35 T100 25 T150 20 T200 15 L200 60 L0 60 Z"
                      fill="url(#land-gradient)"
                      opacity="0.3"
                    />
                    <defs>
                      <linearGradient id="land-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FFD700" />
                        <stop offset="100%" stopColor="transparent" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </motion.div>

              {/* Battery card */}
              <motion.div
                className="visual-card visual-card-secondary"
                style={{ transform: 'translate(40px, -30px)' }}
                whileHover={{ y: -5 }}
              >
                <div className="battery-indicator">
                  <div className="battery-level" style={{ height: '78%' }} />
                </div>
                <div className="battery-info">
                  <span className="battery-percent">78%</span>
                  <span className="battery-label">SOC</span>
                </div>
              </motion.div>
            </div>
          </ScrollReveal>

          <div className="feature-content">
            <ScrollReveal>
              <span className="feature-badge badge-land">Ground Intelligence</span>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <h2 className="feature-title">
                Every sensor.{' '}
                <GradientText gradient="land">Real-time.</GradientText>
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <p className="feature-description">
                Direct hardware integration with Fronius, Huawei, and SMA inverters.
                Monitor 30+ metrics with sub-second latency, control battery dispatch,
                and track performance across every string.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="feature-stats">
                <div className="feature-stat">
                  <span className="feature-stat-value">34</span>
                  <span className="feature-stat-label">Hardware Endpoints</span>
                </div>
                <div className="feature-stat">
                  <span className="feature-stat-value">30+</span>
                  <span className="feature-stat-label">Live Metrics</span>
                </div>
                <div className="feature-stat">
                  <span className="feature-stat-value">6</span>
                  <span className="feature-stat-label">Aggregation Levels</span>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="feature-list">
                {features.map((feature, i) => (
                  <motion.div
                    key={i}
                    className="feature-item"
                    whileHover={{ x: 5 }}
                  >
                    <span className="feature-item-icon">
                      <feature.icon size={20} />
                    </span>
                    <div>
                      <span className="feature-item-title">{feature.title}</span>
                      <span className="feature-item-desc">{feature.desc}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  )
}
